export const APP_LINK_LIST_URL = 'https://d259u2vi7a6l42.cloudfront.net/etc/app-link-list.json';

export type AppLinkMap = Record<string, string>;

const ALLOWED_DOMAINS = [
  'myrealtrip.com',
  'dev-myrealtrip.com',
  'dev01-myrealtrip.com',
  'dev02-myrealtrip.com',
  'test-myrealtrip.com',
  'test01-myrealtrip.com',
  'test02-myrealtrip.com',
  'stage-myrealtrip.com',
];

export function isAllowedDomain(hostname: string): boolean {
  return ALLOWED_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

export function extractSubdomain(hostname: string): string | null {
  for (const domain of ALLOWED_DOMAINS) {
    if (hostname === domain) return null;
    if (hostname.endsWith(`.${domain}`)) {
      const subdomain = hostname.slice(0, -(domain.length + 1));
      return subdomain || null;
    }
  }
  return null;
}

export function findMatchingPattern(
  pathname: string,
  linkMap: AppLinkMap,
): { deepLink: string; params: Record<string, string> } | null {
  // 정확히 일치하는 키 먼저 확인
  if (linkMap[pathname] !== undefined && linkMap[pathname]) {
    return { deepLink: linkMap[pathname], params: {} };
  }

  const pathParts = pathname.split('/').filter(Boolean);

  for (const [pattern, deepLink] of Object.entries(linkMap)) {
    if (!deepLink) continue;

    // key의 `?` 이하 query string은 path 매칭 대상에서 제외 (문서화 역할).
    // 같은 path를 가진 key가 복수이면 Object.entries 순서로 첫 번째 일치가 우선.
    const patternPath = pattern.split('?')[0];
    const patternParts = patternPath.split('/').filter(Boolean);
    if (patternParts.length !== pathParts.length) continue;

    const params: Record<string, string> = {};
    let isMatch = true;

    for (let i = 0; i < patternParts.length; i++) {
      const paramMatch = patternParts[i].match(/^\{(\w+)\}$/);
      if (paramMatch) {
        params[paramMatch[1]] = pathParts[i];
      } else if (patternParts[i] !== pathParts[i]) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      return { deepLink, params };
    }
  }

  return null;
}

export function applyParams(
  template: string,
  pathParams: Record<string, string>,
  queryParams: URLSearchParams,
): string | null {
  let deepLink = template;

  // SAL-1226: split.join으로 모든 occurrence 치환 (String.replace는 첫 번째만 처리해
  // 같은 키가 반복되는 템플릿에서 미치환 placeholder가 남아 null 반환되던 결함 수정).
  // String.replaceAll은 ES2021. consumer(cross-domain 등)의 lib이 ES2020이라 split.join 사용.
  for (const [key, value] of Object.entries(pathParams)) {
    deepLink = deepLink.split(`{${key}}`).join(value);
  }

  const usedQueryParams = new Set<string>();
  queryParams.forEach((value, key) => {
    const placeholder = `{${key}}`;
    if (deepLink.includes(placeholder)) {
      deepLink = deepLink.split(placeholder).join(value);
      usedQueryParams.add(key);
    }
  });

  // 미치환 플레이스홀더가 남아있으면 매칭 실패
  if (deepLink.includes('{') && deepLink.includes('}')) {
    return null;
  }

  const remainingParams = new URLSearchParams();
  queryParams.forEach((value, key) => {
    if (!usedQueryParams.has(key)) {
      remainingParams.append(key, value);
    }
  });

  const queryString = remainingParams.toString();
  if (queryString) {
    const separator = deepLink.includes('?') ? '&' : '?';
    deepLink = `${deepLink}${separator}${queryString}`;
  }

  return deepLink;
}

function createWebViewDeepLink(url: string): string {
  return `mrt://web?url=${encodeURIComponent(url)}`;
}

export function generateDeepLink(webUrl: string, appLinkMap: AppLinkMap | undefined): string {
  if (!webUrl) return createWebViewDeepLink(webUrl);

  let parsed: URL;
  try {
    parsed = new URL(webUrl);
  } catch {
    return createWebViewDeepLink(webUrl);
  }

  const { hostname, pathname, searchParams } = parsed;

  if (appLinkMap) {
    const subdomain = extractSubdomain(hostname);
    const hasSubdomain = subdomain && subdomain !== 'www';

    // 1. 서브도메인 매핑 우선
    if (hasSubdomain) {
      const subdomainPath = `${subdomain}:${pathname}`;
      const match = findMatchingPattern(subdomainPath, appLinkMap);
      if (match) {
        const result = applyParams(match.deepLink, match.params, searchParams);
        if (result) return result;
      }
    }

    // 2. 일반 pathname 매핑
    if (isAllowedDomain(hostname)) {
      const match = findMatchingPattern(pathname, appLinkMap);
      if (match) {
        const result = applyParams(match.deepLink, match.params, searchParams);
        if (result) return result;
      }
    }
  }

  // 3. 폴백
  return createWebViewDeepLink(webUrl);
}
